import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService, { Competition } from '../services/api';

const CompetitionBanner: React.FC = () => {
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    const dismissedId = sessionStorage.getItem('competitionBannerDismissed');
    return dismissedId !== null;
  });

  useEffect(() => {
    const fetchCompetition = async () => {
      try {
        const data = await apiService.getCompetitions({ limit: 1 });
        if (data.competitions && data.competitions.length > 0) {
          const activeCompetition = data.competitions[0];
          setCompetition(activeCompetition);

          // If the competition changed, reset dismiss state
          const dismissedId = sessionStorage.getItem('competitionBannerDismissed');
          if (dismissedId && dismissedId !== activeCompetition.id) {
            sessionStorage.removeItem('competitionBannerDismissed');
            setDismissed(false);
          }
        }
      } catch (err) {
        console.error('Error fetching competition:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompetition();
  }, []);

  const handleDismiss = () => {
    if (competition?.id) {
      sessionStorage.setItem('competitionBannerDismissed', competition.id);
    }
    setDismissed(true);
  };

  // Don't show if loading, dismissed, no competition, or competition is resolved
  if (loading || dismissed || !competition || competition.isResolved) {
    return null;
  }

  // Check if deadline is approaching (within 3 days)
  const isDeadlineApproaching = competition.deadline &&
    new Date(competition.deadline).getTime() - Date.now() < 3 * 24 * 60 * 60 * 1000 &&
    new Date(competition.deadline) > new Date();

  // Format deadline
  const formatDeadline = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffDays > 1) {
      return `${diffDays} dni`;
    } else if (diffDays === 1) {
      return 'Jutro';
    } else if (diffHours > 0) {
      return `${diffHours}h`;
    } else {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return diffMins > 0 ? `${diffMins} min` : 'Ostatnia szansa!';
    }
  };

  return (
    <div className={`${isDeadlineApproaching ? 'bg-amber-500' : 'bg-violet-600'} text-white`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Competition indicator */}
            <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-sm font-semibold">KONKURS</span>
            </div>

            {/* Title */}
            <div className="hidden sm:block">
              <p className="font-medium">
                {competition.title}
              </p>
            </div>

            {/* Deadline warning */}
            {competition.deadline && (
              <div className="hidden md:flex items-center gap-1.5 text-sm opacity-90">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  {isDeadlineApproaching ? 'Zostało: ' : 'Do: '}
                  {formatDeadline(competition.deadline)}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Submit button */}
            <Link
              to="/competitions"
              className={`inline-flex items-center px-4 py-1.5 ${isDeadlineApproaching ? 'bg-white text-amber-600' : 'bg-white text-violet-600'} rounded-full text-sm font-semibold hover:bg-opacity-90 transition-colors`}
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Weź udział
            </Link>

            {/* Dismiss button */}
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Zamknij"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitionBanner;
